import { AppShell, Box, LoadingOverlay, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { navigationDataMappedByLink } from "../../constants/navigation";
import { Breadcrumb } from "./breadcrumb";
import { useTokenVars, DEFAULT_THEME } from "@dehub/theme";
import { Header } from "./header";
import { Navigation } from "./navbar";
import classes from "./styles.module.css";

// Fixed DeHub indigo for the back-office. The shared header chrome
// (avatar chip, profile / notifications / apps menus) reads `--dehub-primary`
// directly, and `useTokenVars()` would set it to the resolved tenant brand
// (e.g. ZAX red). Pin it to the DeHub palette so the back-office never adopts
// a tenant color.
const DEHUB_PRIMARY = DEFAULT_THEME.theme.tokens.colors.primary[6];

export function Layout() {
  useTokenVars();
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--dehub-primary", DEHUB_PRIMARY);
    root.style.setProperty("--dehub-on-primary", "#FFFFFF");
  });
  const location = useLocation();
  const parentSection = location.pathname.split("/")[1];
  const parentNavigation = navigationDataMappedByLink[`/${parentSection}`] || {};
  const [opened] = useDisclosure();
  return (
    <AppShell padding={0} header={{ height: 60 }} navbar={{ width: 112, breakpoint: "sm", collapsed: { mobile: !opened } }}>
      <Header section={parentNavigation} />
      <Navigation />
      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

type PageProps = {
  title?: string | React.JSX.Element;
  subTitle?: string | React.JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  breadcrumb?: { to?: string; label: string }[];
  bgColor?: string;
  loading?: boolean;
};

export function PageLayout({ title, subTitle, breadcrumb, extra, children, loading = false }: PageProps) {
  return (
    <Box className={classes.pageLayout}>
      <LoadingOverlay visible={loading} />
      {breadcrumb && <Breadcrumb data={breadcrumb} />}

      <Box className={classes.titleLayout}>
        <Box>
          <Title order={2} className={classes.title}>
            {title}
          </Title>
          <Text className={classes.subTitle}>{subTitle}</Text>
        </Box>
        <Box className={classes.extra}>
          {(extra || [])?.map((item, index) => (
            <Box key={index}>{item}</Box>
          ))}
        </Box>
      </Box>
      <Box className={classes.content}>{children}</Box>
    </Box>
  );
}

type FormProps = {
  title: string | React.JSX.Element;
  subTitle?: string | React.JSX.Element;
  extra?: React.ReactNode[];
  children: React.ReactNode;
  bgColor?: string;
  my?: number | string;
};

export function FormLayout({ title, subTitle, bgColor = "white", extra = [], children, my = 20 }: FormProps) {
  return (
    <Box
      className={classes.formLayout}
      my={my}
      style={{
        backgroundColor: bgColor,
      }}>
      <Box className={classes.titleLayout}>
        <Box>
          <Title order={2} className={classes.title}>
            {title}
          </Title>
          <Text className={classes.subTitle}>{subTitle}</Text>
        </Box>
        <Box className={classes.extra}>{extra}</Box>
      </Box>
      <Box className={classes.content}>{children}</Box>
    </Box>
  );
}

interface IFilterProps {
  left?: React.JSX.Element[];
  right?: React.JSX.Element[];
}

export function PageFilter({ left, right }: IFilterProps) {
  return (
    <Box className={classes.pageFilter}>
      <Box className={classes.sectionElement}>{left}</Box>
      <Box className={classes.sectionElement}>{right}</Box>
    </Box>
  );
}
